import type { Experiment, ExperimentSchedule } from 'types';
import type { PowerAnalysisResult, PowerProjection } from 'statisticsdb/types';
import { TooltipProvider } from '@/components/ui/tooltip';
import { MetricLabel } from './MetricLabel';
import { cn } from 'helpers/cn';
import { formatPercentage } from 'helpers/formatters';
import { useSchedule } from '@/state/experiments/useSchedule';

const DAY_MS = 86_400_000;

// With the year: a projection can be up to a year out, and "3 Mar" alone is ambiguous.
const shortDate = (epoch: number) =>
  new Date(epoch).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

/** "1.20 pts", not "1.20% pts": these are percentage points, not a percentage. */
const points = (rate: number) => `${(rate * 100).toFixed(2)} pts`;

/**
 * The payload shape comes from statisticsdb/types, not a local copy.
 *
 * Every field here was optional because the hand-written copy in this file could
 * not know what the server actually sends, which is how the dashboard ended up
 * blind to fields the server had added. Cached rows written before a field existed
 * are a real case, so those stay optional, but explicitly rather than by omission.
 */
type CachedPowerAnalysis = Omit<
  PowerAnalysisResult,
  'observedEffect' | 'controlRate' | 'resolvable' | 'sizedFor' | 'projection'
> &
  Partial<Pick<PowerAnalysisResult, 'observedEffect' | 'controlRate' | 'resolvable' | 'sizedFor' | 'projection'>>;

type PowerAnalysisProps = {
  power: CachedPowerAnalysis | null;
  totalSessions: number;
  experiment: Experiment;
};

export const PowerAnalysis = ({ power, totalSessions, experiment }: PowerAnalysisProps) => {
  const { schedule } = useSchedule(experiment.id);
  if (!power) return null;

  // Enough power reads ok; short of it is a decision to make (run longer, or accept a
  // coarser effect), which is warn; too little to be worth reading is danger.
  const powerPct = Math.round(power.power * 100);
  const powerColor = powerPct >= 80 ? 'text-ok' : powerPct >= 50 ? 'text-warn' : 'text-danger';
  const powerFill = powerPct >= 80 ? 'bg-ok' : powerPct >= 50 ? 'bg-warn' : 'bg-danger';

  // Measure the run over the window it was actually live, not up to today.
  const runEnd = experiment.endsAt ?? Date.now();
  const daysRunning = experiment.startsAt ? Math.max(0, Math.floor((runEnd - experiment.startsAt) / 86_400_000)) : 0;
  const projection: PowerProjection = power.projection ?? fallbackProjection(power, !!experiment.endsAt);

  return (
    <TooltipProvider>
      <div className="border-line bg-panel2 rounded-card border">
        <div className="border-line border-b px-4 py-3">
          <h3 className="text-foreground text-title font-medium tracking-[-0.01em]">Power analysis</h3>
          <p className="supporting">
            Evaluate whether your experiment has enough data to detect meaningful differences.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-3">
          <div className="border-line bg-panel rounded-card space-y-2 border p-3">
            <MetricLabel metric="statisticalPower" className="micro-label">
              Statistical power
            </MetricLabel>
            <p className={cn('metric-value', powerColor)}>{powerPct}%</p>
            {/* The track is a token, the fill is the same colour as the figure above it,
                so the bar cannot disagree with the number. */}
            <div className="bg-track rounded-pill h-1 w-full overflow-hidden">
              <div className={cn('rounded-pill h-full', powerFill)} style={{ width: `${Math.min(powerPct, 100)}%` }} />
            </div>
            <p className="supporting">
              {powerPct >= 80 ? 'Sufficient power to detect effects' : 'More data needed for reliable results'}
            </p>
          </div>

          <div className="border-line bg-panel rounded-card space-y-2 border p-3">
            <MetricLabel metric="resolvableEffect" className="micro-label">
              Resolvable effect
            </MetricLabel>
            <p className="metric-value text-foreground">{points(power.mde)}</p>
            <ResolvableBand power={power} />
          </div>

          <div className="border-line bg-panel rounded-card space-y-2 border p-3">
            <MetricLabel
              metric={projection.state === 'finished' ? 'requiredSampleSize' : 'timeRemaining'}
              className="micro-label"
            >
              {projection.state === 'finished' ? 'Run length' : 'Estimated time remaining'}
            </MetricLabel>
            <p className="metric-value text-foreground">{projectionHeadline(projection, daysRunning)}</p>
            <p className="supporting">{projectionCaption(projection, power, totalSessions)}</p>
            <StopWarning projection={projection} stopAt={pendingStop(schedule, !!experiment.endsAt)} />
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

/**
 * The measured change set against the floor.
 *
 * Reporting the floor alone left the key question unanswered: an effect below it
 * cannot be resolved at this traffic level no matter how long the experiment runs,
 * which reads as "no result" when it is really "not enough traffic to ever tell".
 */
const ResolvableBand = ({ power }: { power: CachedPowerAnalysis }) => {
  const { observedEffect, controlRate, resolvable, mde } = power;

  // controlRate is absent on payloads cached before this existed; fall back to the
  // previous copy rather than rendering a comparison against nothing.
  if (observedEffect === undefined || controlRate === undefined) {
    return (
      <p className="supporting">
        Smallest absolute change in conversion rate this experiment can reliably detect at the current sample size, in
        percentage points, not relative lift
      </p>
    );
  }

  const relative = controlRate > 0 ? (observedEffect / controlRate) * 100 : 0;
  const canResolve = resolvable ?? observedEffect >= mde;

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="micro-label">Measuring</span>
        <span className={cn('num', canResolve ? 'text-ok' : 'text-warn')}>
          {points(observedEffect)}
          {controlRate > 0 && <span className="text-dim"> ({formatPercentage(relative)} rel.)</span>}
        </span>
      </div>
      <p className={cn('supporting', !canResolve && 'text-warn')}>
        {canResolve
          ? 'The difference being measured is above the floor, so this traffic can resolve it.'
          : 'The difference being measured is below the floor: at this traffic level it cannot be resolved, however long the experiment runs.'}
      </p>
    </div>
  );
};

/**
 * A stop the scheduler will still carry out, or null.
 *
 * The schedule row is returned whatever its state, and a blocked or completed row
 * keeps its stopAt: warning that one "comes first" would describe a stop that is not
 * going to happen. A stop already in the past is the same case seen from the clock.
 */
const pendingStop = (schedule: ExperimentSchedule | null, ended: boolean): number | null => {
  if (ended || !schedule?.stopAt) return null;
  if (schedule.state !== 'armed' && schedule.state !== 'active') return null;
  return schedule.stopAt > Date.now() ? schedule.stopAt : null;
};

/**
 * Whether the scheduled stop arrives before the sample does.
 *
 * The projection and the schedule were two numbers on two tabs, and nothing put them
 * side by side: a test could be scheduled to stop a week before it could possibly
 * conclude, and would then end inconclusive exactly as planned.
 */
const StopWarning = ({ projection, stopAt }: { projection: PowerProjection; stopAt: number | null }) => {
  if (stopAt === null) return null;
  if (projection.state !== 'estimated' && projection.state !== 'unreachable') return null;

  const finishAt = Date.now() + projection.days * DAY_MS;
  if (finishAt <= stopAt) {
    return <p className="supporting text-ok">Should finish before the scheduled stop on {shortDate(stopAt)}.</p>;
  }

  return (
    <p className="supporting text-warn">
      The scheduled stop on {shortDate(stopAt)} comes first: at this rate it ends before reaching the sample needed.
    </p>
  );
};

/** Older cached payloads predate `projection`; derive an equivalent. */
const fallbackProjection = (power: CachedPowerAnalysis, finished: boolean): PowerProjection => {
  if (finished) return { state: 'finished' };
  if (power.estimatedDaysRemaining === null) return { state: 'unknown' };
  if (power.estimatedDaysRemaining === 0) return { state: 'reached' };
  return { state: 'estimated', days: power.estimatedDaysRemaining };
};

const projectionHeadline = (projection: PowerProjection, daysRunning: number) => {
  switch (projection.state) {
    case 'reached':
      return 'Sufficient data';
    case 'finished':
      return `${daysRunning} days`;
    case 'estimated':
      return `~${projection.days} days (${shortDate(Date.now() + projection.days * DAY_MS)})`;
    case 'unreachable':
      return 'Over a year';
    case 'unknown':
      return 'Not enough data';
  }
};

const projectionCaption = (projection: PowerProjection, power: CachedPowerAnalysis, totalSessions: number) => {
  const sessions = totalSessions.toLocaleString();
  switch (projection.state) {
    case 'finished':
      return `Experiment has ended: ${sessions} total sessions. No further data will arrive.`;
    case 'reached':
      return `${sessions} total sessions, past the ${power.requiredSampleSize.toLocaleString()} per variant needed ${sizedForCopy(power)}.`;
    case 'estimated':
      return `At the current rate, to reach the ${power.requiredSampleSize.toLocaleString()} sessions per variant needed ${sizedForCopy(power)}.`;
    case 'unreachable':
      return `Current traffic will not reach the ${power.requiredSampleSize.toLocaleString()} sessions per variant needed ${sizedForCopy(power)} within a year (~${projection.days} days).`;
    case 'unknown':
      return `${sessions} total sessions: too early to project a rate.`;
  }
};

/**
 * Which effect the sample-size target was computed against. Sizing for the
 * measured difference answers "when does what I am seeing become conclusive";
 * the fixed fallback only applies when there is no measurable difference to size
 * against.
 */
const sizedForCopy = (power: CachedPowerAnalysis) => {
  if (power.sizedFor === 'observed' && power.observedEffect) {
    return `to confirm the ${(power.observedEffect * 100).toFixed(2)}pt difference currently measured`;
  }
  return 'to detect a 5% relative lift, since no difference is measurable yet';
};
